/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
/* eslint-disable no-multi-spaces */
/* eslint-disable no-unused-vars */

// @ts-ignore
import {MouseEventHandler} from 'react';
import {Theme} from '@material-ui/core/styles';
import {Moment} from 'moment';
import {AccountHashMapKeyType} from '@ion/core/src/entities/Accounts';
import {IApplicationFeatures} from '@ion/core/src/security/ApplicationFeatures';
import {
  CTCEntity,
  CVSAssetType,
  DateFilterOptions,
  GenericObject,
  SmarterTrailerIssues,
  TrailerDoorStatus,
  TrailerStatus
} from '@ion/core/src/types/entities';
import {DateType} from '@ion/core/src/utils/DateUtil';
import {FilterParamsProps} from '../filters/useFilterSubmit';
// @ts-ignore
import json from '../theme/theme.json5';

const {colors} = json.dls;

/**
 * @deprecated The Color Enum should no longer be used in order to facilitate
 * runtime theming. Please use the {@link dlsColor} API from `@ion/dls/src/theme`
 * instead.
 *
 * Provides the enumeration of all DLS color definitions as per the dls
 * colors section of the theme.json5.
 */
export enum Color {
  aaBlue      = colors.aaBlue,
  caBlue      = colors.caBlue,
  black       = colors.black,
  grey1       = colors.grey1,
  grey2       = colors.grey2,
  grey3       = colors.grey3,
  grey4       = colors.grey4,
  grey5       = colors.grey5,
  grey6       = colors.grey6,
  grey8       = colors.grey8,
  white       = colors.white,
  arcticBlue  = colors.arcticBlue,
  darkBlue    = colors.darkBlue,
  darkTeal    = colors.darkTeal,
  lightBlue   = colors.lightBlue,
  lightTeal   = colors.lightTeal,
  paleBlue    = colors.paleBlue,
  skyBlue     = colors.skyBlue,
  caGreen     = colors.caGreen,
  caYellow    = colors.caYellow,
  caRed       = colors.caRed,
  transparent = colors.transparent,
  caOrange    = colors.caOrange
};

// defines the general dls breakpoints type definition ...
export type DLSBreakPoints = {
  mobile: number;
  tablet: number;
  desktop: number
};

// defines the top-level dls layout type definition ...
export type DLSLayout = {
  layout: {
    breakpoints: DLSBreakPoints;
    gutters: DLSBreakPoints;
    header: DLSBreakPoints;
    sidebar: {
      width: number;
      accentBreakpoint: number;
      mobileWidth: number
    };
    columns: number;
    margins: number;
    spacing: number;
  }
};

// defines the top-level dls color type definition ...
export type DLSColors = {
  aaBlue: Color;
  caBlue: Color;
  black: Color;
  grey1: Color;
  grey2: Color;
  grey3: Color;
  grey4: Color;
  grey5: Color;
  grey6: Color;
  white: Color;
  arcticBlue: Color;
  darkBlue: Color;
  darkTeal: Color;
  lightBlue: Color;
  lightTeal: Color;
  paleBlue: Color;
  skyBlue: Color;
  caGreen: Color;
  caYellow: Color;
  caRed: Color;
};

// [DLS TODO]: Define specific types for all typographic elements
// defines the top-level dls typography type definition ...
export type DLSTypography = {
  h1: any;
  h2: any;
  h3: any;
  h4: any;
  body1: any;
  body2: any;
  body3: any;
  title: any;
  sidebar: any;
  caption: any;
  tags: any;
  cta: any;
  chartText: any;
  dataCaption: any;
  tabs: any;
  chips: any;
  chipButton: any;
  pagination: any;
  slider: any;
  panelCards: any;
};

// defines the top-level dls motion type definition ...
export type DLSMotion = {
  easing: {
    standard: string;
    accelerated: string;
    decelerated: string;
  };
  duration: {
    t1: string;
    t2: string;
    t3: string;
    t4: string;
    t5: string;
  };
  element: {
    largeExpansion: {
      open: string;
      close: string;
    };
    smallExpansion: {
      open: string;
      close: string;
    };
  };
};

// defines the top-level dls alternative type definition ...
export type DLSAlt = {
  maps: any;
  typography: any;
  sizes: any;
  components: any;
};

// defines the top-level dls type definition ...
export type DLS = {
  layout: DLSLayout;
  colors: DLSColors;
  typography: DLSTypography;
  motion: DLSMotion;
  alt: DLSAlt
};

// defines the dls specific material ui theme type definition ...
export interface DLSTheme extends Theme {
  dls: DLS;
};

// defines the icon size enum definitions ...
export enum IconSize {
  small      = 16,
  medium     = 24,
  large      = 40,
  extraLarge = 80
};

// defines the icon size enum definitions ...
export enum StatusIconSize {
  small = 8,
  basic = 10
};

// defines the icon status enum definitions ...
export enum IconStatus {
  active  = colors.caGreen,
  towed   = colors.caOrange,
  idle    = colors.caYellow,
  offline = colors.grey3,
  parked  = colors.grey4,
  issue   = colors.caRed,
  on      = colors.caGreen,
  off     = colors.caRed,
  nogps   = colors.caOrange
};

// defines the icon size props definition ...
export interface IconSizeProps {
  size?: IconSize;
};

// defines the icon color props definition ...
export interface IconColorProps {
  color?: Color;
  stroke?: Color;
  iconColor?: string;
};

export interface IconOpacity {
  opacity?: number;
}

// defines the icon props definition ...
export interface IconProps extends IconSizeProps, IconColorProps, IconOpacity {}

// icon button icon directions
export enum IconButtonDirectionEnum {
  left  = 'left',
  right = 'right',
  stack = 'stack',
  up    = 'up',
  down  = 'down'
};

// header typography
export enum HeaderTypographyVariantEnum {
  h1 = 'h1',
  h2 = 'h2',
  h3 = 'h3',
  h4 = 'h4'
};

// Label typography
export enum LabelTypographyVariantEnum {
  label1 = 'label1',
  label2 = 'label2',
  label3 = 'label3',
};

// body typography
export enum BodyTypographyVariantEnum {
  body1 = 'body1',
  body2 = 'body2'
};

//Data cards
export enum DataCardVariantEnum {
  xs      = 'xs',
  small   = 'small',
  modular = 'modular'
}

// defines the i18n localized props definition ...
export interface LocalizedProps {
  t: any;
};

// Navigation Icon Direction
export enum NavigationIconDirectionEnum {
  back    = 'back',
  forward = 'forward'
};

// ExpandCollapse Icon Direction
export enum ExpandCollapseIconDirectionEnum {
  up   = 'up',
  down = 'down'
};

// defines the prop definitions for the LocationTool
export interface MarkerProps {
  onClick: MouseEventHandler,
  size: IconSize,
  color: Color,
  isSelected?: boolean,
  disabled?: boolean
};

// Base Map types
export enum BaseMapEnum {
  grayVector              = 'gray-vector',
  hybrid                  = 'hybrid',
  satellite               = 'satellite',
  osm                     = 'osm',
  streetsNavigationVector = 'streets-navigation-vector',
  topoVector              = 'topo-vector'
};

// define color mode enum for the view
export enum ColorModeEnum {
  light = 'light',
  dark  = 'dark'
};

// Toolbar Enum to Show/Hide the view
export enum ToolbarEnum {
  Layers        = 'Layers',
  SearchMap     = 'SearchMap',
  Geofence      = 'Geofence',
  Basemap       = 'Basemap',
  SavedMapViews = 'SavedMapViews'
}

// Map UI tools position enum
export enum MapToolsPositionEnum {
  bottomLeft  = 'bottom-left',
  bottomRight = 'bottom-right',
  topRight    = 'top-right',
  topLeft     = 'top-left'
}

// define map tools position index
export enum MapToolIndex {
  BaseMapGallery = 0,
  MapLayerChoosers = 1,
  SearchView = 2,
  CreateGeofence = 3,
  SaveMapView = 4,
  TopRightTool = 5,
  BottomRightTool = 6
}

// SideSheet origin options enum
export enum SideSheetOrigin {
  bottom = 'bottom',
  top    = 'top',
  left   = 'left',
  right  = 'right'
}

//Available Asset SideSheet Views ...
export enum SideSheetView {
  detailed = 'detailed',
  collapsed = 'collapsed',
  table = 'table',
  bottomTable = 'bottomTable'
}

// enum for breadcrumb actions on the sidesheet
export enum BreadcrumbActions {
  backClick = 'backClick',
  rootClick = 'rootClick'
};

// enum for breadcrumb state on the sidesheet
export enum BreadcrumbState {
  ASSETS        = 1,
  ASSET_DETAILS = 2,
  FIND_NEARBY   = 3,
  TOOL_DETAILS  = 3
};

// enum for iOn Tag Architecture
export enum TagArchitecture {
  SCI = 'SCI'
}

// Asset Label types
export interface AssetLabelTypes {
  status: string;
  address: string;
  group: string;
  routeName?: string;
  iOnTags: string;
  assetDetail: string;
  batteryLevel: string;
  lastReported: string;
  chargeLevel: string;
  gpsSignal: string;
  gpsNotCurrent: string;
  noGps: string;
  distance?: string;
  away?: string;
}

// defines the filter props definitions
export interface FilterOptions{
  title: string;
  items: Array<any>;
  setSelectedFilter: Function;
  showCount?: boolean;
  defaultOptionIndex?: number;
  selectAll?: boolean;
  selected: Array<any>;
  multiSelect: boolean;
  addOns: Array<{title: string, method: Function}>;
  groups: Array<{object}>;
  key: string;
};

export type FilterProps = {
  filters: FilterOptions[];
};

// defines the filter items props definition ...
export interface FilterItems {
  loading: boolean;
  error: string | null | boolean;
  items: Array<any>;
  selected: Array<any>;
};

// defines the enumeration for filter option ...
export enum FilterOption {
  label = 'label',
  value = 'value',
  id = 'id',
  name = 'name',
  objectId = 'OBJECTID',
  landmarkName = 'landmarkName',
  assetName = 'assetName',
  eventName = 'iOnEventName',
  eventCode = 'eventCode'
};

// defines the selected filter props definition
export type SavedFilterProps = {
  accountMap?: AccountHashMapKeyType | null;
  initialFilters?: TrackingFilters;
  filters?: FilterOptions[];
  magicKey?: string;
  text?: string;
};

export interface GeofenceFilterItems extends FilterItems {
  open?: boolean;
};

export type BoundaryFilterType = number[][]

// defines the tracking filters props definition
export type TrackingFilters = {
  groups?: FilterItems;
  assets?: FilterItems;
  devices?: FilterItems;
  assetTypes?: FilterItems;
  assetCategories?: FilterItems;
  geoFences?: GeofenceFilterItems;
  routes?: FilterItems;
  assetStatus?: FilterItems;
  date?: DateFilterOptions;
  events?: FilterItems;
  selected?: FilterItems;
  boundary?: BoundaryFilterType;
}

// Defines the Hitory Input Events props
export type HistoryInputEvents = {
  label: string;
  isSelected: boolean;
  color?: string;
  assetNames?: string[];
};

// Defines the Hitory Input Events props
export type HistoryInputEventsColorBar = {
  value: number,
  label?: string,
  adornment?: string,
  width?: number;
  height?: number;
  color?: string;
  labelColor?: string;
  fontStyle?: string
};

// Defines the Event Line Layer props
export type EventLineLayer = {
  uniqueValues: Object[],
  visualVariables: Object[],
  reportField: string,
  color: string
};

// Defines the Last Asset Position
export type LastAssetPosition = {
  assets: CVSAssetType[],
  value: number,
  inputs: HistoryInputEvents[],
  sort: string
};

// Map marker Layers enum
export enum MapLayerTypeEnum {
  AssetLayer                = 'AssetLayer',
  AssetHistoryLayer         = 'AssetHistoryLayer',
  TripHistoryLayer          = 'TripHistoryLayer',
  EventLineLayer            = 'EventLineLayer',
  OperationalLayer          = 'OperationalLayer',
  GeofenceLayer             = 'GeofenceLayer',
  BoundaryLayer             = 'BoundaryLayer',
  SupplyChainDeviceLayer = 'SupplyChainDeviceLayer'
};

// map layer settings enum
export enum MapLayerSettings {
  CurrentLayerSettings              = 'CurrentLayerSettings',
  TripHistoryLayerSettings          = 'TripHistoryLayerSettings',
  GeofenceLayerSettings             = 'GeofenceLayerSettings',
  TrafficLayerSettings              = 'TrafficLayerSettings',
  WeatherLayerSettings              = 'WeatherLayerSettings',
  OperationalLayersSettings         = 'OperationalLayersSettings',
  SupplyChainDeviceLayerSettings = 'SupplyChainDeviceLayerSettings'
}

// defines the icon size enum definitions ...
export enum AvatarIconSize {
  small  = 24,
  medium = 32,
  large  = 80,
  extraLarge = 104,
  max = 140
}

// Create GeoFence types enum
export enum CreateGeoFenceTypeEnum {
  Radial  = 'Radial',
  Polygon = 'Polygon',
  Draw = 'Draw',
  Custom = 'Custom'
}

// Types of filters
export enum AllFilterTypes {
  Groups = 'groups',
  Assets = 'assetTypes',
  Categories = 'assetCategories',
  Geofences = 'geoFences',
  Date = 'date'
}

export enum NotificationPanelTabs {
  allActivity = 1,
  urgent = 0
}

// We need a string to display empty values in a select
// field but an enum to compare the values
export enum EmptySelectLabels {
  none = 'none'
}

// Skeleton TextSize
export enum SkeletonTextSizeEnum {
  medium = 'medium',
  small = 'small'
};

/**
 * Hard-code Map Layers
 */
export enum CurrentMapLayer {
  CVS            = 'CVS',
  CVSServer      = 'CVS Server',
  HVS            = 'HVS',
  Weather        = 'Weather (Radar NA)',
  Traffic        = 'Traffic',
  WeatherLegend  = 'Weather (Radar NA) Legend',
  Geofence       = 'Geofence',
  GeofenceAlerts = 'Geofence Alerts',
  CrashBoxxICN   = 'CrashBoxx ICN',
  BLETools       = 'BLE Tools'
};

/**
 * ArcGIS Layer Types
 */
export enum ArcGISLayerType {
  featureLayer         = 'ArcGISFeatureLayer',
  mapServiceLayer      = 'ArcGISMapServiceLayer',
  webTiledLayer        = 'WebTiledLayer',
  tiledMapServiceLayer = 'ArcGISTiledMapServiceLayer',
  imageServiceLayer    = 'ArcGISImageServiceLayer'
};

// defines the type definitions for partial success state ...
export type PartialSuccessType = {
  results: CTCEntity[] | [];
  items: CTCEntity[] | [];
};

/**
 * History Report Type
 * Event is changed to Input in the label
 */
export enum HistoryReportTypeEnum {
  Asset = 'Asset',
  Event = 'Event'
};

/**
 * History Input Events
 */
export enum HistoryInputEventsEnum {
  Mixed = 'mixed',
  None  = 'none'
};

/**
 * History Report Type Field to the Renderer
 */
export enum HistoryReportTypeFieldEnum {
  Asset = 'assetId',
  Event = 'reportType'
};

/**
 * Defines the prop definitions for common filter props ...
 *
 * elementId:                The ID of the element which is used for Pseudo logger.
 * name                      The current filter name.
 * filterState:              The state of asset type filters.
 * isMobileView:             Checks if mobile view.
 * onSubmit:                 Callback fired with the selected value
 *                           when the primary button is clicked.
 * onCancel:                 Callback fired when the secondary button is clicked.
 * onMenuClose:              Callback fired when the mobile menu is closed.
 * primaryButtonDisabled:    Checks if the primary button has to be disabled.
 * secondaryButtonDisabled:  Checks if the secondary button has to be disabled.
 * showSelectedOptionsCount: Checks if the selected options count
 *                           has to be displayed.
 */
export interface FilterPropsType {
  elementId?: string;
  name: string | string[];
  filterState?: FilterItems | GenericObject;
  isMobileView?: boolean;
  onSubmit: (filterId: string | string[], filterParams: FilterParamsProps) => void;
  onCancel?: () => void;
  onMenuClose?: () => void;
  primaryButtonDisabled?: boolean;
  secondaryButtonDisabled?: boolean;
  showSelectedOptionsCount?: boolean;
  accountId?: number | number[];
  forceRefresh?: boolean;
  resetForceRefresh?: Function;
};

export enum Preference {
  basemap = 'basemap'
}
export interface AssetsMap {
  [key: string]: Array<any>
}

export interface MapviewInitialExtent {
  label: string,
  extent: object
};

export interface SmarterTrailerWidgetInfo {
  maxValue?: number;
  units?: string;
  messageTime: DateType;
  timestamp: string | DateType;
  minValue?: number;
  batteryLevel?: TrailerStatus;
  doorState?: TrailerDoorStatus;
  issues?: SmarterTrailerIssues
}

export interface SavedMapViewType {
  id: string;
  extent: __esri.Extent;
  basemap: string;
  isDefault?: boolean;
}

export type MapViews = {
  savedMapViews: SavedMapViewType[];
  defaultMapView?: SavedMapViewType;
}

export type LastFilteredDateProps = {
  startDate: Moment;
  endDate: Moment;
  setFilterStartDate: Function;
  setFilterEndDate: Function;
  selectedDateFilterValue: DateSelectorValues;
}

export interface CachedData {
  [id: string]: CachedItem
}

export interface CachedItem {
  id: number | string;
  title: string;
  key: number | string;
  accountId: number | string;
  isAsset: boolean;
  isDevice: boolean;
}

// defines the enumeration for tracking filters ...
export enum Filters {
  assets = 'assets',
  groups = 'groups',
  devices = 'devices',
  assetTypes = 'assetTypes',
  assetCategories = 'assetCategories',
  events = 'events',
  date = 'date',
  boundary = 'boundary',
  routes = 'routes',
  assetStatus = 'assetStatus',
  geoFences = 'geoFences',
  categories = 'categories',
  types = 'types'
};

/**
 * Defines the position of the View SubGroups/SubAccounts toggle control on a page.
 */
export enum ViewSubGroupsPosition {
  GRID_CTAS = 'GRID_CTAS',
  GRID_PAGINATION_CONTROLS = 'GRID_PAGINATION_CONTROLS'
}

/**
 * Defines the props at the page level for configuring the View SubGroups/SubAccounts control.
 */
export interface PageViewSubGroupsProps {
  Features?: IApplicationFeatures;
  enableSubgroupToggle?: boolean;
  isCtcAdmin?: boolean;
  viewSubGroupsPosition?: ViewSubGroupsPosition;
}

/**
 * Defines the props for managing the View SubGroups/SubAccounts control.
 */
export interface ViewSubGroupsProps {
  hideSubGroups?: boolean;
  viewSubGroups?: boolean;
  isCtcAdmin?: boolean;
  toggleViewSubGroups?: (view: boolean) => void;
}

export interface ViewInventoryOnlyProps {
  toggleViewInventoryOnly?: (view: boolean) => void;
  viewInventoryOnly?: boolean;
  hideInventoryOnly?: boolean;
}

// Defines the props for managing the rows per page dropdown.
export interface GridPageSizeProps {
  gridPageSize?: number;
  isCtcAdmin?: boolean;
  usePreferedPageSize?: boolean;
  setGridPageSize?: (pageSize: number) => void;
}

export interface ModularSearchProps {
  autoSelectNext?: boolean;
}

// defines the sensor status enum definitions ...
export enum SensorStatus {
  normal  = colors.caBlue,
  alert   = colors.caRed,
};

// defines the service status codes for maintainence dashboard
export enum ScheduleStatusServiceFields {
  Scheduled = 0,
  Upcomming = 1,
  Overdue = 2
};

//defines the SC related props passed down to tree filter components.
export interface SCFilterProps {
  hasSCIAccess: boolean;
  onCheckIncludeSCDevices?: Function;
  includeSCDevice?: boolean;
  SCDeviceTypes?: string;
};

//defines type of request to map the data filtered by current global group.
export enum RequestTreeDataType {
  Assets = 'assets',
  Devices = 'devices'
}

//defines type of redux actions triggered from the tree filter
export enum TreeUpdateType {
  SET_GROUP_FILTERS_CHECKED = 'SET_GROUP_FILTERS_CHECKED',
  SET_GROUPS_NODE_KEY_MAP = 'SET_GROUPS_NODE_KEY_MAP',
  SET_GROUP_FILTERS_EXPANDED = 'SET_GROUP_FILTERS_EXPANDED'
};

//defines types of cached data to be filted
export enum CachedDataType {
  isAsset = 'isAsset',
  isDevice = 'isDevice'
};

export enum DateSelectorValues {
  Live = 'live',
  Today = 'today',
  Yesterday = 'yesterday',
  LastSeven = 'lastSeven',
  LastThirty = 'lastThirty',
  Custom = 'custom'
};

export enum TrackingMode {
  Live = 'live',
  History = 'history'
};

export enum DTCCodes {
  SPN = 'spn',
  SourceAddress = 'sourceAddress',
  FMI = 'fmi'
};

export enum DtcEventType {
  Jpod2DTCReport = 'jpod2DTCReport',
  Dtc2Events = 'dtc2Events',
  DtcEvents = 'dtcEvent'
};

export type LabelContentType = {
  primaryLabel: string,
  secondaryLabel?: string | null
};

// marker interface as type is being used but definition is not found
export type ViewSupplementaryOnlyProps = {};
